import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Search, Clock, Phone, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { ScreenHeader } from '../../components/layout/ScreenHeader';
import { API_BASE_URL } from '../../config';
import { useAppContext } from '../../hooks/useAppContext';
import { differenceInHours, differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';
import { Skeleton } from '../../components/common/Skeleton';
import ModernAdminPackageManagementScreen from './ModernAdminPackageManagementScreen';

// Types aligned with employee list screen for consistency
type PackageStatus = 'NEW' | 'PAYMENT_CONFIRMED' | 'PROCESSING' | 'READY_FOR_DELIVERY' | 'DELIVERED' | 'CANCELLED';
interface PackageRequest {
  id: string;
  title: string;
  description: string;
  customerName: string;
  customerPhone: string;
  priority: 'low' | 'medium' | 'high';
  status: PackageStatus;
  progressPercent: number;
  creationDate: string;
  lastModified: string;
  employeeId: string;
  employeeName?: string;
  branch: string;
  customerLocation?: string;
  meta?: Record<string, any>;
}

const statusBadge = (status: PackageStatus) => {
  switch (status) {
    case 'NEW': return <Badge>جديد</Badge>;
    case 'PAYMENT_CONFIRMED': return <Badge className="bg-blue-500/10 text-blue-500">تم تأكيد الدفع</Badge>;
    case 'PROCESSING': return <Badge className="bg-amber-500/10 text-amber-600">قيد المعالجة</Badge>;
    case 'READY_FOR_DELIVERY': return <Badge className="bg-purple-500/10 text-purple-600">جاهز للتسليم</Badge>;
    case 'DELIVERED': return <Badge variant="success">تم التسليم</Badge>;
    case 'CANCELLED': return <Badge variant="destructive">ملغي</Badge>;
    default: return <Badge>غير معروف</Badge>;
  }
};

const timeAgo = (isoDate: string) => {
  const date = new Date(isoDate);
  const hours = differenceInHours(new Date(), date);
  if (hours < 24) return `${hours} ساعة مضت`;
  const days = differenceInDays(new Date(), date);
  return `${days} يوم مضى`;
};

const AdminPackageManagementScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user, designMode } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PackageStatus | 'ALL'>('ALL');
  const [packages, setPackages] = useState<PackageRequest[]>([]);
  const [offline, setOffline] = useState(false);
  const [listAnimReady, setListAnimReady] = useState(false);

  if (designMode === 'modern') {
    return <ModernAdminPackageManagementScreen />;
  }

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/package-requests`, {
          headers: user ? { 'X-User-Id': user.id, 'X-User-Role': user.role } : {}
        });
        if (!res.ok) throw new Error(`خطأ في استجابة الخادم: ${res.status}`);
        const data = await res.json().catch(() => []);
        setPackages(Array.isArray(data) ? data : []);
        setOffline(false);
      } catch (err) {
        console.error('Failed to load package requests', err);
        setOffline(true);
        setPackages([]);
        toast.error('تعذر الاتصال بالخادم. يرجى المحاولة لاحقاً.');
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, [user]);

  const filtered = useMemo(() => {
    if (!Array.isArray(packages)) return [];
    return packages.filter(p => {
      const matchesQuery = [p.title, p.customerName, p.employeeName, p.branch].some(v => String(v || '').toLowerCase().includes(query.toLowerCase()));
      const matchesStatus = statusFilter === 'ALL' ? true : p.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [packages, query, statusFilter]);

  useEffect(() => {
    if (!loading) {
      setListAnimReady(false);
      const t = setTimeout(() => setListAnimReady(true), 0);
      return () => clearTimeout(t);
    }
  }, [loading, filtered.length, statusFilter, query]);

  return (
    <div className="space-y-6">
      <ScreenHeader
        icon={Package}
        title={'إدارة البكجات للمسؤول'}
        colorClass="bg-nav-workflow"
        onBack="/"
        actionButton={
          <div className="flex items-center gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="ابحث بعنوان الطلب أو العميل"
              leftIcon={Search}
            />
          </div>
        }
      />

      <div className="flex gap-2 flex-wrap">
        {(['ALL', 'NEW', 'PAYMENT_CONFIRMED', 'PROCESSING', 'READY_FOR_DELIVERY', 'DELIVERED', 'CANCELLED'] as const).map(s => (
          <Button
            key={s}
            variant={statusFilter === s ? 'default' : 'secondary'}
            className={`relative transition-all duration-150 active:scale-95 hover:-translate-y-[1px] focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 ${statusFilter === s ? 'shadow-sm ring-1 ring-primary/30 after:content-[\'\'] after:absolute after:top-0 after:left-0 after:w-full after:h-[2px] after:bg-primary/30' : ''}`}
            onClick={() => setStatusFilter(s as any)}
          >
            {s === 'ALL' ? 'الكل' : statusBadge(s as PackageStatus)}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="p-4">
              <div className="flex items-center justify-between mb-3">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-5 w-20 rounded" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-28" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-16" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-44" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex items-center justify-center h-40"><p>لا توجد طلبات حالياً</p></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((req, idx) => (
            <Card
              interactive
              key={req.id}
              style={{ transitionDelay: `${idx * 40}ms` }}
              className={`relative cursor-pointer group transition-all duration-300 hover:shadow-xl hover:-translate-y-[2px] hover:scale-[1.01] active:scale-95 ring-1 ring-transparent hover:ring-primary/20 shadow-none ${listAnimReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}
              onClick={() => navigate(`/admin-packages/${req.id}`, { state: { initial: req } })}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base truncate">{req.title || 'طلب بدون عنوان'}</CardTitle>
                  {statusBadge(req.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="space-y-1 text-slate-600 dark:text-slate-300">
                  <div className="flex items-center gap-2">
                    <User size={14} />
                    <span className="truncate">{req.employeeName || req.employeeId}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${['PAYMENT_CONFIRMED', 'PROCESSING', 'READY_FOR_DELIVERY', 'DELIVERED'].includes(req.status) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {['PAYMENT_CONFIRMED', 'PROCESSING', 'READY_FOR_DELIVERY', 'DELIVERED'].includes(req.status) ? 'مدفوع' : 'غير مدفوع'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={14} />
                    <span className="truncate">{req.customerName} - {req.customerPhone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={14} />
                    <span className="truncate">{timeAgo(req.creationDate)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPackageManagementScreen;
